/* oxlint-disable max-lines-per-function */
// This file houses Uppy plugin interactions.

import { useCallback, useMemo } from 'react';

import AwsS3 from '@uppy/aws-s3';
import type { Body, Meta } from '@uppy/core';
import Uppy from '@uppy/core';
import DropTarget from '@uppy/drop-target';

import type { TranslationKey } from '@change-corgi/core/i18n';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';
import type { ImageLocation } from '@change-corgi/middle-end-gql-schema';

import { getPhotoUploadCredentials } from './api';
import { MAX_IMAGE_FILE_SIZE, UPLOAD_ERROR_MESSAGES } from './shared/constants';
import { dataUriToBlob, dataUriToBlobAsync } from './shared/utils/dataUriToBlob';
import { handlePhotoFile } from './shared/utils/handlePhotoFile';
import { getUploadErrorReason, UploadError } from './shared/utils/uploadError';
import type { UploaderControls, UploadResultType, UppyFileType } from './types';

type UseUploaderProps = (errorMessage: TranslationKey) => void;

export const handleRestrictionFailed =
	(handleError: UseUploaderProps) =>
	(_file: UppyFileType | undefined, error: Error): void => {
		handleError(UPLOAD_ERROR_MESSAGES[getUploadErrorReason(error.message)]);
	};

function assertUploadSucceeded(uploadResult: UploadResultType): void {
	const failed = uploadResult?.failed ?? [];
	const successful = uploadResult?.successful ?? [];
	if (failed.length === 0 && successful.length > 0) return;

	const failureDetail = failed[0]?.error ?? undefined;
	throw new UploadError(getUploadErrorReason(failureDetail ?? ''), failureDetail);
}

// oxlint-disable-next-line eslint/max-lines-per-function
export function useUploader(
	handleError: UseUploaderProps,
	maxNumberOfFiles: number | null | undefined = 1,
	maxFileSize: number = MAX_IMAGE_FILE_SIZE,
): UploaderControls {
	const uppy = useMemo(
		() =>
			new Uppy<Meta, Body>({
				restrictions: {
					maxNumberOfFiles,
					allowedFileTypes: ['image/*'],
					maxFileSize,
				},
			}),
		// oxlint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	uppy.on('restriction-failed', handleRestrictionFailed(handleError));

	// The UploadContainer will house ImageUploader when this Uppy listener is triggered.
	// The file added here and the file that Uppy stores is from the initial drag and drop or system select.
	// Once there is an image, the UploadContainer will house ImageCropper, to handle repositioning the image.
	// If the image is removed by a user via OnCancelReposition in the ImageCropper,
	// onImageChange updates the image to be null and Uppy removes the original file.
	const onFileAdded: UploaderControls['onFileAdded'] = useCallback(
		(onImageChange) => {
			uppy.on('file-added', (file) => {
				// This check is needed to prevent an infinite loop
				if (file.source === 'DropTarget' || file.source === 'react:FileInput') {
					handlePhotoFile(file, onImageChange);
				}
			});
		},
		[uppy],
	);

	const getFile = useCallback(() => {
		return uppy.getFiles().at(0);
	}, [uppy]);

	const onFileAddedTracking: UploaderControls['onFileAddedTracking'] = useCallback(
		(trackingEvents) => {
			uppy.on('file-added', (file) => {
				const uppyFile = getFile();
				if (!uppyFile) return;
				const { type } = uppyFile;
				if (file.source === 'DropTarget') {
					trackingEvents({ uploadMethod: 'drop_file', fileType: type || '' });
				}
				if (file.source === 'react:FileInput') {
					trackingEvents({ uploadMethod: 'browse_files', fileType: type || '' });
				}
			});
		},
		[getFile, uppy],
	);

	const onUseDropTarget = useCallback((): void => {
		if (!uppy.getPlugin('DropTarget')) {
			uppy.use(DropTarget, {
				target: document.body,
			});
		}
	}, [uppy]);

	const onUseAwsS3 = useCallback(
		(utilityContext: UtilityContext, imageLocation: ImageLocation): void => {
			uppy.use(AwsS3, {
				id: Math.random().toString().split('.')[1],
				shouldUseMultipart: false,
				getUploadParameters: async (file) => {
					const uploadCredentials = await getPhotoUploadCredentials(file.name, utilityContext, imageLocation);
					uppy.setFileMeta(file.id, { filePublicUrl: uploadCredentials?.filePublicUrl });
					return {
						method: 'POST',
						url: uploadCredentials?.postUrl ?? '',
						// oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
						fields: (uploadCredentials?.s3formData ?? {}) as Record<string, string>,
					};
				},
			});
		},
		[uppy],
	);

	const upload = useCallback(async (): Promise<UploadResultType> => {
		const isUppyError = !!uppy.getState().error;
		const uploadResult = isUppyError ? await uppy.retryAll() : await uppy.upload();
		assertUploadSucceeded(uploadResult);
		return uploadResult;
	}, [uppy]);

	const removeFile = useCallback(
		(id: string): void => {
			uppy.removeFile(id);
		},
		[uppy],
	);

	const addFile = useCallback(
		(fileType: string | undefined, image: Blob | string): void => {
			const blob = typeof image === 'string' ? dataUriToBlob(image) : image;
			uppy.addFile({
				name: `addFile${Math.random().toString().split('.')[1]}.jpeg`,
				type: fileType,
				data: blob,
			});
		},
		[uppy],
	);
	// This function is similar to addFile, but it supports async fetching of image URLs.
	// This function was added because `fromBase64` in the dataUriToBlob function was not converting the uri correctly.
	const addFileAsync = useCallback(
		async (image: Blob | string): Promise<void> => {
			let blob: Blob;
			if (typeof image === 'string') {
				blob = await dataUriToBlobAsync(image);
			} else {
				blob = image;
			}
			uppy.addFile({
				name: `addFile${Math.random().toString().split('.')[1]}.${blob.type.split('/')[1] || 'jpeg'}`,
				type: blob.type,
				data: blob,
			});
		},
		[uppy],
	);

	const updateImage = useCallback(
		(blob: Blob): void => {
			const uppyFile = getFile();
			if (!uppyFile) return;
			if (!uppyFile.id || !uppyFile.type) return;
			const { id, type: fileType } = uppyFile;
			removeFile(id);
			addFile(fileType, blob);
		},
		[addFile, getFile, removeFile],
	);

	const clearImage = useCallback((): void => {
		const file = getFile();
		if (file) removeFile(file.id);
	}, [getFile, removeFile]);

	return {
		uppy,
		onFileAdded,
		onFileAddedTracking,
		onUseDropTarget,
		onUseAwsS3,
		upload,
		removeFile,
		addFile,
		addFileAsync,
		getFile,
		updateImage,
		clearImage,
	};
}
